/**
 * Гидравлические расчеты для калькулятора расхода воды в трубах
 */

/**
 * Расчет кинематической вязкости воды в зависимости от температуры
 * 
 * @param {number} temperature - Температура воды (°C)
 * @returns {number} - Кинематическая вязкость (м²/с)
 */
export const getWaterViscosity = (temperature) => {
  // Значения вязкости в диапазоне температур 0-100°C
  // Аппроксимация формулой на основе справочных данных
  return 1.792e-6 * Math.exp(-0.0301 * temperature);
};

/**
 * Расчет плотности воды в зависимости от температуры
 * 
 * @param {number} temperature - Температура воды (°C)
 * @returns {number} - Плотность (кг/м³)
 */
export const getWaterDensity = (temperature) => {
  // Аппроксимация плотности воды в диапазоне 0-100°C
  if (temperature < 0 || temperature > 100) {
    console.warn('Температура воды выходит за пределы 0-100°C');
  }
  
  return 1000 - 0.0178 * Math.pow(temperature - 4, 1.7);
};

/**
 * Расчет числа Рейнольдса
 * 
 * @param {Object} params - Параметры расчета
 * @param {number} params.diameter - Внутренний диаметр трубы (м)
 * @param {number} params.flowRate - Расход воды (м³/ч)
 * @param {number} params.temperature - Температура воды (°C)
 * @returns {number} - Число Рейнольдса
 */
export const calculateReynoldsNumber = ({ diameter, flowRate, temperature = 20 }) => {
  // Переводим расход из м³/ч в м³/с
  const flowRateM3s = flowRate / 3600;
  
  // Скорость потока (м/с)
  const velocity = flowRateM3s / (Math.PI * Math.pow(diameter / 2, 2));
  
  // Кинематическая вязкость воды (м²/с)
  const viscosity = getWaterViscosity(temperature);
  
  // Число Рейнольдса
  return (velocity * diameter) / viscosity;
};

/**
 * Расчет коэффициента гидравлического трения по формуле Альтшуля
 * 
 * @param {number} reynoldsNumber - Число Рейнольдса
 * @param {number} relativeRoughness - Относительная шероховатость (шероховатость/диаметр)
 * @returns {number} - Коэффициент гидравлического трения
 */
export const calculateFrictionFactor = (reynoldsNumber, relativeRoughness) => {
  // Для ламинарного режима
  if (reynoldsNumber < 2300) {
    return 64 / reynoldsNumber;
  }
  
  // Формула Альтшуля для переходного и турбулентного режима
  // Работает во всем диапазоне чисел Рейнольдса более 2300
  return 0.11 * Math.pow(relativeRoughness + 68 / reynoldsNumber, 0.25);
};

/**
 * Расчет потерь давления на трение
 * 
 * @param {Object} params - Параметры расчета
 * @param {number} params.diameter - Внутренний диаметр трубы (м)
 * @param {number} params.length - Длина трубы (м)
 * @param {number} params.velocity - Скорость потока (м/с)
 * @param {number} params.density - Плотность жидкости (кг/м³)
 * @param {number} params.frictionFactor - Коэффициент гидравлического трения
 * @returns {number} - Потери давления (Па)
 */
export const calculateFrictionLosses = ({ diameter, length, velocity, density, frictionFactor }) => {
  return frictionFactor * (length / diameter) * (density * Math.pow(velocity, 2) / 2);
};

/**
 * Расчет потерь давления на местных сопротивлениях
 * 
 * @param {Object} params - Параметры расчета
 * @param {number} params.velocity - Скорость потока (м/с)
 * @param {number} params.density - Плотность жидкости (кг/м³)
 * @param {number} params.localResistance - Суммарный коэффициент местных сопротивлений
 * @returns {number} - Потери давления (Па)
 */
export const calculateLocalLosses = ({ velocity, density, localResistance }) => {
  return localResistance * (density * Math.pow(velocity, 2) / 2);
};

/**
 * Расчет гидростатического давления при изменении высоты
 * 
 * @param {Object} params - Параметры расчета
 * @param {number} params.elevationChange - Изменение высоты (м, положительное - подъем)
 * @param {number} params.density - Плотность жидкости (кг/м³)
 * @returns {number} - Гидростатическое давление (Па)
 */
export const calculateElevationPressure = ({ elevationChange, density }) => {
  const g = 9.81; // Ускорение свободного падения (м/с²)
  return density * g * elevationChange;
};

/**
 * Расчет расхода воды на основе перепада давления
 * 
 * @param {Object} params - Параметры расчета
 * @param {number} params.diameter - Внутренний диаметр трубы (м)
 * @param {number} params.length - Длина трубы (м)
 * @param {number} params.roughness - Абсолютная шероховатость трубы (м)
 * @param {number} params.pressureDrop - Перепад давления (бар)
 * @param {number} params.temperature - Температура воды (°C)
 * @param {number} params.localResistance - Суммарный коэффициент местных сопротивлений
 * @param {number} params.elevationChange - Изменение высоты (м, положительное - подъем)
 * @returns {Object} - Результат расчета с расходом, скоростью и другими параметрами
 */
export const calculateFlowRate = ({ 
  diameter, 
  length, 
  roughness, 
  pressureDrop, 
  temperature = 20, 
  localResistance = 0,
  elevationChange = 0
}) => {
  // Перевод бар в Па
  const pressureDropPa = pressureDrop * 100000;
  
  // Получаем физические свойства воды
  const density = getWaterDensity(temperature);
  const viscosity = getWaterViscosity(temperature);
  
  // Относительная шероховатость
  const relativeRoughness = roughness / diameter;
  
  // Гидростатическое давление из-за изменения высоты
  const elevationPressure = calculateElevationPressure({ elevationChange, density });
  
  // Доступное давление для преодоления гидравлических сопротивлений
  let availablePressure = pressureDropPa - elevationPressure;
  
  // Если весь перепад давления уходит на подъем (или даже не хватает), расход будет нулевым или отрицательным
  if (availablePressure <= 0) {
    return {
      flowRate: 0,
      velocity: 0,
      pressureDrop: pressureDrop,
      diameter: diameter,
      elevationPressureDrop: elevationPressure / 100000, // в бар
    };
  }
  
  // Итерационный расчет, так как коэффициент трения зависит от скорости (числа Рейнольдса)
  // Начинаем с предположения о ламинарном потоке
  let frictionFactor = 0.03; // Начальное приближение
  let velocity = 0;
  let reynolds = 0;
  
  // Определение скорости потока через уравнение Дарси-Вейсбаха
  // Решаем итерационно, так как коэффициент трения зависит от режима течения
  for (let i = 0; i < 20; i++) { // Макс. 20 итераций для сходимости
    // Рассчитываем скорость из уравнения потерь давления (решение уравнения Дарси-Вейсбаха)
    const denominator = density * (frictionFactor * length / diameter + localResistance);
    velocity = Math.sqrt((2 * availablePressure) / denominator);
    
    // Число Рейнольдса для этой скорости
    reynolds = (velocity * diameter) / viscosity;
    
    // Уточняем коэффициент трения
    const newFrictionFactor = calculateFrictionFactor(reynolds, relativeRoughness);
    
    // Проверяем сходимость
    if (Math.abs(newFrictionFactor - frictionFactor) < 0.0001) {
      frictionFactor = newFrictionFactor;
      break;
    }
    
    frictionFactor = newFrictionFactor;
  }
  
  // Расчет расхода (м³/с) по скорости
  const flowRateM3s = velocity * Math.PI * Math.pow(diameter / 2, 2);
  
  // Перевод расхода в м³/ч
  const flowRateM3h = flowRateM3s * 3600;
  
  // Расчет потерь давления
  const frictionLosses = calculateFrictionLosses({ 
    diameter, 
    length, 
    velocity, 
    density, 
    frictionFactor 
  });
  
  const localLosses = calculateLocalLosses({ 
    velocity, 
    density, 
    localResistance 
  });
  
  return {
    flowRate: flowRateM3h,
    velocity: velocity,
    reynoldsNumber: reynolds,
    frictionFactor: frictionFactor,
    frictionLosses: frictionLosses / 100000, // в бар
    localLosses: localLosses / 100000, // в бар
    elevationPressureDrop: elevationPressure / 100000, // в бар
    totalPressureDrop: pressureDrop
  };
};

/**
 * Расчет перепада давления на основе расхода воды
 * 
 * @param {Object} params - Параметры расчета
 * @param {number} params.diameter - Внутренний диаметр трубы (м)
 * @param {number} params.length - Длина трубы (м)
 * @param {number} params.roughness - Абсолютная шероховатость трубы (м)
 * @param {number} params.flowRate - Расход воды (м³/ч)
 * @param {number} params.temperature - Температура воды (°C)
 * @param {number} params.localResistance - Суммарный коэффициент местных сопротивлений
 * @param {number} params.elevationChange - Изменение высоты (м, положительное - подъем)
 * @returns {Object} - Результат расчета с перепадом давления, скоростью и другими параметрами
 */
export const calculatePressureDrop = ({
  diameter,
  length,
  roughness,
  flowRate,
  temperature = 20,
  localResistance = 0,
  elevationChange = 0
}) => {
  // Получаем физические свойства воды
  const density = getWaterDensity(temperature);
  const viscosity = getWaterViscosity(temperature);
  
  // Относительная шероховатость
  const relativeRoughness = roughness / diameter;
  
  // Перевод расхода из м³/ч в м³/с
  const flowRateM3s = flowRate / 3600;
  
  // Скорость потока (м/с)
  const velocity = flowRateM3s / (Math.PI * Math.pow(diameter / 2, 2));
  
  // Число Рейнольдса
  const reynolds = (velocity * diameter) / viscosity;
  
  // Коэффициент гидравлического трения
  const frictionFactor = calculateFrictionFactor(reynolds, relativeRoughness);
  
  // Потери давления на трение (Па)
  const frictionLosses = calculateFrictionLosses({
    diameter,
    length,
    velocity,
    density,
    frictionFactor
  });
  
  // Потери давления на местных сопротивлениях (Па)
  const localLosses = calculateLocalLosses({
    velocity,
    density,
    localResistance
  });
  
  // Гидростатическое давление (Па)
  const elevationPressure = calculateElevationPressure({
    elevationChange,
    density
  });
  
  // Общий перепад давления (Па)
  const totalPressureDropPa = frictionLosses + localLosses + elevationPressure;
  
  // Перевод Па в бар
  const totalPressureDropBar = totalPressureDropPa / 100000;
  
  return {
    pressureDrop: totalPressureDropBar,
    velocity: velocity,
    reynoldsNumber: reynolds,
    frictionFactor: frictionFactor,
    frictionLosses: frictionLosses / 100000, // в бар
    localLosses: localLosses / 100000, // в бар
    elevationPressureDrop: elevationPressure / 100000, // в бар
    totalPressureDrop: totalPressureDropBar,
    flowRate: flowRate
  };
};

/**
 * Расчет оптимального диаметра трубы на основе требуемого расхода и максимально допустимого перепада давления
 * 
 * @param {Object} params - Параметры расчета
 * @param {number} params.flowRate - Требуемый расход воды (м³/ч)
 * @param {number} params.length - Длина трубы (м)
 * @param {number} params.roughness - Абсолютная шероховатость трубы (м)
 * @param {number} params.maxPressureDrop - Максимально допустимый перепад давления (бар)
 * @param {number} params.temperature - Температура воды (°C)
 * @param {number} params.localResistance - Суммарный коэффициент местных сопротивлений
 * @param {number} params.elevationChange - Изменение высоты (м, положительное - подъем)
 * @returns {Object} - Результат расчета с диаметром, скоростью и другими параметрами
 */
export const calculatePipeDiameter = ({
  flowRate,
  length,
  roughness,
  maxPressureDrop,
  temperature = 20,
  localResistance = 0,
  elevationChange = 0
}) => {
  // Получаем физические свойства воды
  const density = getWaterDensity(temperature);
  const viscosity = getWaterViscosity(temperature);
  
  // Перевод бар в Па
  const maxPressureDropPa = maxPressureDrop * 100000;
  
  // Гидростатическое давление (Па)
  // Для предварительного расчета используем произвольный диаметр, так как гидростатическое давление 
  // не зависит от диаметра, а только от высоты и плотности
  const elevationPressure = calculateElevationPressure({
    elevationChange,
    density
  });
  
  // Доступный перепад давления для преодоления гидравлических сопротивлений
  const availablePressure = maxPressureDropPa - elevationPressure;
  
  // Если весь перепад давления уходит на подъем, требуется бесконечно большой диаметр
  if (availablePressure <= 0) {
    return {
      diameter: Infinity,
      velocity: 0,
      reynoldsNumber: 0,
      pressureDrop: maxPressureDrop,
      elevationPressureDrop: elevationPressure / 100000, // в бар
      errorMessage: 'Недостаточно давления для подъема воды на заданную высоту'
    };
  }
  
  // Перевод расхода из м³/ч в м³/с
  const flowRateM3s = flowRate / 3600;
  
  // Итерационный расчет диаметра
  // Начинаем с предположения о значении коэффициента трения
  let frictionFactor = 0.03; // Начальное приближение
  let diameter = 0;
  let velocity = 0;
  let reynolds = 0;
  
  // Используем итерационный метод для уточнения диаметра
  for (let i = 0; i < 20; i++) { // Макс. 20 итераций для сходимости
    // Из преобразованного уравнения Дарси-Вейсбаха:
    // Δp = (ρ/2) * (λL/D + Σζ) * v²
    // Выразим диаметр D, зная все остальные параметры:
    
    // Сначала найдем скорость из расхода и диаметра (если у нас уже есть предположение о диаметре)
    if (diameter > 0) {
      velocity = flowRateM3s / (Math.PI * Math.pow(diameter / 2, 2));
      reynolds = (velocity * diameter) / viscosity;
      frictionFactor = calculateFrictionFactor(reynolds, roughness / diameter);
    }
    
    // Теперь решаем для диаметра, используя уравнение для потерь давления
    const numerator = frictionFactor * length * flowRateM3s * flowRateM3s;
    const denominator = 0.4 * Math.PI * Math.PI * (availablePressure - ((density * localResistance * flowRateM3s * flowRateM3s) / (2 * Math.PI * Math.PI * Math.pow(diameter / 2, 4))));
    
    const newDiameter = Math.pow(numerator / denominator, 0.2);
    
    // Проверяем сходимость
    if (Math.abs(newDiameter - diameter) < 0.0001) {
      diameter = newDiameter;
      break;
    }
    
    diameter = newDiameter;
  }
  
  // Итоговая скорость потока
  velocity = flowRateM3s / (Math.PI * Math.pow(diameter / 2, 2));
  
  // Число Рейнольдса
  reynolds = (velocity * diameter) / viscosity;
  
  // Относительная шероховатость
  const relativeRoughness = roughness / diameter;
  
  // Уточняем коэффициент трения
  frictionFactor = calculateFrictionFactor(reynolds, relativeRoughness);
  
  // Рассчитываем фактический перепад давления для уточненных параметров
  const frictionLosses = calculateFrictionLosses({
    diameter,
    length,
    velocity,
    density,
    frictionFactor
  });
  
  const localLosses = calculateLocalLosses({
    velocity,
    density,
    localResistance
  });
  
  // Общий перепад давления (Па)
  const actualPressureDropPa = frictionLosses + localLosses + elevationPressure;
  
  // Перевод Па в бар
  const actualPressureDropBar = actualPressureDropPa / 100000;
  
  return {
    diameter: diameter, // в метрах
    velocity: velocity,
    reynoldsNumber: reynolds,
    frictionFactor: frictionFactor,
    frictionLosses: frictionLosses / 100000, // в бар
    localLosses: localLosses / 100000, // в бар
    elevationPressureDrop: elevationPressure / 100000, // в бар
    actualPressureDrop: actualPressureDropBar,
    maxPressureDrop: maxPressureDrop,
    flowRate: flowRate
  };
};